'use client';

import { useEffect, useRef, useState } from 'react';
import { Spinner } from './Spinner';
import { Icon } from './Icon';
import { cn } from '@workspace/ui/lib/utils';

type EmojiItem = {
  slug: string;
  char: string;
  name: string;
  group: string;
  subGroup: string;
};

type EmojiPickerProps = {
  onSelect: (emoji: string) => void;
  children?: React.ReactNode;
  isReply?: boolean;
};

export const EmojiPicker = ({ onSelect, children, isReply }: EmojiPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [emojis, setEmojis] = useState<EmojiItem[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const pickerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const controller = new AbortController();

    const timeout = setTimeout(async () => {
      try {
        setLoading(true);

        const params = new URLSearchParams();
        if (search.trim()) {
          params.set('search', search.trim());
        }

        const res = await fetch(`/api/emojis?${params.toString()}`, {
          signal: controller.signal,
        });

        if (!res.ok) {
          throw new Error('Failed to fetch emojis');
        }

        const data: EmojiItem[] = await res.json();
        setEmojis(data.slice(0, 120));
      } catch (e: any) {
        if (e.name !== 'AbortError') {
          console.error('Emoji fetch error:', e);
          setEmojis([]);
        }
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [search, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(e: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }

    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen]);

  const handleSelectEmoji = (emoji: string) => {
    onSelect(emoji);
    setIsOpen(false);
  };

  const handleToggle = () => {
    setIsOpen((prev) => {
      const next = !prev;
      if (!next) {
        setSearch('');
        setEmojis([]);
      } else {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      }
      return next;
    });
  };

  const handleClearSearch = () => {
    setSearch('');
    inputRef.current?.focus();
  };

  return (
    <div className="relative md:h-5" ref={pickerRef}>
      <button type="button" onClick={handleToggle} className={cn("size-5 flex items-center justify-center", isOpen && 'text-pink-accent')}>
        {children}
      </button>

      {isOpen && (
        <div className={cn("absolute -top-24 -right-30 z-50 mt-2 w-60 rounded-md border bg-white shadow-lg p-2", isReply && 'left-0 right-0')}>
          <div className="relative mb-2">
            <input
              ref={inputRef}
              type="text"
              className="w-full border border-gray-200 rounded px-2 py-1 pr-6 text-sm focus:outline-none focus:ring-0"
              placeholder="Search emoji..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 hover:cursor-pointer bg-black rounded-full"
              >
                <Icon name="x" className="size-3" />
              </button>
            )}
          </div>

          {loading && (
            <div className="flex items-center justify-center py-6">
              <span className="mx-auto">
                <Spinner />
              </span>
            </div>
          )}

          {!loading && (
            <div className="max-h-20 overflow-auto grid grid-cols-8 gap-1 text-xl">
              {emojis.map((emoji) => (
                <button
                  key={emoji.slug}
                  type="button"
                  className="hover:bg-gray-100 rounded text-center"
                  onClick={() => handleSelectEmoji(emoji.char)}
                >
                  {emoji.char}
                </button>
              ))}

              {emojis.length === 0 && search && (
                <div className="col-span-8 text-xs text-gray-500 text-center py-2">
                  No emojis found
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
